import { useState } from "react";

import { FormGroup, InputGroup } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Check, CopyIcon } from "lucide-react";

import { copyToClipboard } from "@/components/helpers/browser/copy-to-clipboard";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { Box } from "@/components/layout/flexbox";
import { Button } from "@/components/ui/button";
import { SUBTEXT_COLOR } from "@/css/constants";

interface SharePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  target: React.ReactElement;
}

export function SharePopover({
  isOpen,
  onClose,
  shareUrl,
  target,
}: SharePopoverProps) {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    try {
      await copyToClipboard(shareUrl);
      setCopySuccess(true);

      // Reset copy success state after 3 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      content={
        <PopoverContent>
          <Box mb={3}>
            <h4>Share this calculator configuration</h4>
            <Box color={SUBTEXT_COLOR} fontSize={14}>
              Anyone with this link will be able to see your calculator
              configuration.
            </Box>
          </Box>

          <FormGroup>
            <StyledInputGroup
              value={shareUrl}
              readOnly
              fill
              onClick={(e) => e.currentTarget.select()}
            />
            <Box mt={2}>
              <Button onClick={handleCopy} className="w-full">
                {copySuccess ? <Check /> : <CopyIcon />}
                {copySuccess ? "Copied!" : "Copy"}
              </Button>
            </Box>
          </FormGroup>
        </PopoverContent>
      }
      interactionKind="click"
      placement="bottom-start"
      usePortal={true}
      minimal={false}
      autoFocus={false}
    >
      {target}
    </Popover>
  );
}

const PopoverContent = styled(Box)`
  padding: 16px;
  width: 400px;
  max-width: 90vw;

  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
  }
`;

const StyledInputGroup = styled(InputGroup)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-right: -1px;

  &:focus {
    z-index: 1;
  }
`;
